import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
  onSearchUpdate?: (query: string) => void
  renderResults?: (onResultClick?: () => void) => JSX.Element | JSX.Element[]
  placeholder?: string
}

export const SearchModal = ({
  isOpen,
  onClose,
  onSearchUpdate,
  renderResults,
  placeholder = 'Search',
}: SearchModalProps): React.ReactNode => {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // Global keyboard shortcut to open search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k' && !isOpen) {
        e.preventDefault()
        if (inputRef.current) {
          // Trigger focus which will open the modal via SearchBar
          const event = new Event('focus', { bubbles: true })
          inputRef.current.dispatchEvent(event)
        }
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value
    setQuery(newQuery)
    if (onSearchUpdate) {
      onSearchUpdate(newQuery)
    }
  }

  const handleClear = () => {
    setQuery('')
    if (onSearchUpdate) {
      onSearchUpdate('')
    }
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  // Only render via portal when open
  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div
        className="relative w-full max-w-2xl mx-4 mt-20"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Container */}
        <div className="bg-white rounded-none shadow-[0_20px_60px_rgba(0,0,0,0.3)] border border-contentareaborder overflow-hidden z-[9999]">
          {/* Header with search input */}
          <div className="flex items-center px-4 py-3 border-b border-contentareaborder bg-contentareabackgroundalt">
            <FontAwesomeIcon
              icon={faSearch}
              className="text-actionable mr-3"
              size="lg"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder={placeholder}
              className="flex-1 font-body text-lg text-body bg-transparent outline-none placeholder:text-darkgray/70"
              autoComplete="off"
            />
            {query && (
              <button
                onClick={handleClear}
                className="ml-2 p-1 rounded-full hover:bg-white text-darkgray hover:text-secondary transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} size="sm" />
              </button>
            )}
            <button
              onClick={onClose}
              className="ml-3 px-3 py-1 text-sm font-body text-secondary hover:text-actionable transition-colors"
            >
              Cancel
            </button>
          </div>

          {/* Results Container */}
          {query.trim() && (
            <div className="max-h-96 overflow-y-auto">
              {renderResults ? (
                renderResults(onClose)
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No results found
                </div>
              )}
            </div>
          )}

          {/* Empty state when no query */}
          {!query.trim() && (
            <div className="p-8 text-center text-darkgray/80">
              <FontAwesomeIcon
                icon={faSearch}
                size="2x"
                className="mb-4 text-actionable"
              />
              <p>Start typing to search profiles and events</p>
            </div>
          )}
        </div>

        {/* Keyboard shortcut hint */}
        <div className="text-center mt-4 text-sm font-body text-secondary rounded-full bg-white/40 backdrop-blur-[2px] z-[9999] p-4">
          Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">ESC</kbd>{' '}
          to close
          {' • '}
          <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">⌘K</kbd> to
          search
        </div>
      </div>
    </div>
  )

  // Render into document.body via portal
  return createPortal(modalContent, document.body) as React.ReactNode
}
